import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { identifyUser, resetUser } from './posthog';
import { reportClientError } from './utils/error-notification';
import { clearCorruptedAuthData } from './clear-corrupted-auth';
import { isAdminByEmail } from './admin-utils';
import { createSafeStorage, safeLocalStorage } from './safe-storage';

// Debounce utility function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

interface User {
  id: number;
  email: string;
  name?: string;
  role?: 'admin' | 'client';
  isSuperAdmin?: boolean;
  plan?: string;
  // payment_completed removed - payment status managed at agency level
}

interface AdminUser {
  id: number;
  email: string;
  name?: string;
  role: 'admin';
  isSuperAdmin?: boolean;
}


interface Plan {
  id: string;
  name: string;
  price: number;
  credits_per_month: number;
  rate_limit_per_hour: number;
  included_endpoints: string[];
  description: string;
  features: string[];
}

interface BillingInfo {
  creditsUsed: number;
  creditsTotal: number;
  billingStartDate: string;
  billingEndDate: string;
  planName: string;
  planId: string;
  usagePercentage: number;
  activeDateCycle: {
    from: string;
    to: string;
    daysRemaining: number;
    isActive: boolean;
  };
}

interface AuthState {
  // Core auth state
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error?: string;
  
  // Admin-specific state
  isAdmin: boolean;
  
  // API Keys state
  
  // Plan and billing state
  activePlan: Plan | null;
  billingInfo: BillingInfo | null;
  planLoading: boolean;
  
      // Actions
      setUser: (user: User | null) => void;
      setError: (error: string | undefined) => void;
      setLoading: (loading: boolean) => void;
      signOut: () => void;
      initialize: () => void;
      clearCorruptedData: () => void;
  
  // Debounced auth actions
  debouncedInitialize: () => void;
  debouncedRefreshAuth: () => void;
  
  // API Keys actions
  
  // Plan and billing actions
  fetchBillingInfo: () => Promise<void>;
  setBillingInfo: (info: BillingInfo) => void;
  setActivePlan: (plan: Plan | null) => void;
  fetchPlanDetails: (planId: string) => Promise<void>;
  updatePlan: (planId: string) => Promise<boolean>;
}

// Type guards for better type safety
export const isAdminUser = (user: User | null): user is AdminUser => {
  return user !== null && (
    user.role === 'admin' ||
    user.isSuperAdmin === true ||
    (user.email ? isAdminByEmail(user.email) : false)
  );
};

// Utility function to get admin user safely
export const getAdminUser = (user: User | null): AdminUser | null => {
  return isAdminUser(user) ? user as AdminUser : null;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Core auth state
      user: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      error: undefined,
      
      // Admin-specific state
      isAdmin: false,
      
      // API Keys state
      
      // Plan and billing state
      activePlan: null,
      billingInfo: null,
      planLoading: false,
      setUser: (user) => {
        try {
          // Identify user in PostHog
          if (user) {
            identifyUser(user.id, {
              email: user.email,
              name: user.name,
              role: user.role,
              lastActive: new Date().toISOString()
            });
          }

          // Determine if user is admin
          const isAdmin = Boolean(user && (isAdminByEmail(user.email) || user.role === 'admin' || user.isSuperAdmin === true));

          set({ 
            user, 
            isAuthenticated: !!user,
            isInitialized: true,
            isAdmin,
            isLoading: false,
            error: undefined
          });
          
          // Update localStorage with the new user data
          if (typeof window !== 'undefined' && user) {
            safeLocalStorage.setItem('user', JSON.stringify(user));
          }
        } catch (error) {
          reportClientError(
            error as Error,
            'AuthStore.setUser',
            { 
              operation: 'setUser',
              userId: user?.id,
              userEmail: user?.email
            }
          );
          // Still set the user even if PostHog or localStorage fails
          set({ 
            user, 
            isAuthenticated: !!user,
            isInitialized: true,
            isAdmin: Boolean(user && (isAdminByEmail(user.email) || user.role === 'admin' || user.isSuperAdmin === true)),
            isLoading: false,
            error: undefined
          });
        }
      },
      setError: (error) => set({ error, isLoading: false }),
      setLoading: (loading) => set({ isLoading: loading }),
      signOut: () => {
        try {
          // Clear localStorage immediately
          if (typeof window !== 'undefined') {
            // Clear specific auth keys
            safeLocalStorage.removeItem('user');
            safeLocalStorage.removeItem('token');
            safeLocalStorage.removeItem('auth-storage');
            
            // Clear app store (contains agency data including logo)
            safeLocalStorage.removeItem('app-store');
            
            // Clear all agency-related and client-related data
            // Note: We try native localStorage for key iteration, fall back gracefully if unavailable
            try {
              const keysToRemove: string[] = [];
              if (window.localStorage) {
                for (let i = 0; i < window.localStorage.length; i++) {
                  const key = window.localStorage.key(i);
                  if (key && (
                    key.startsWith('agency-client-')
                  )) {
                    keysToRemove.push(key);
                  }
                }
                
                // Remove all agency/client keys
                keysToRemove.forEach(key => safeLocalStorage.removeItem(key));
              }
            } catch (err) {
              console.warn('Could not iterate localStorage keys:', err);
            }
          }
          
          set({ 
            user: null, 
            isAuthenticated: false,
            isInitialized: true,
            isAdmin: false,
            isLoading: false,
            error: undefined
          });
          resetUser();
        } catch (error) {
          reportClientError(
            error as Error,
            'AuthStore.signOut',
            { operation: 'signout' }
          );
          // Still clear the state even if localStorage fails
          set({ 
            user: null, 
            isAuthenticated: false,
            isInitialized: true,
            isAdmin: false,
            isLoading: false,
            error: undefined
          });
        }
      },

      clearCorruptedData: () => {
        try {

          if (typeof window !== 'undefined') {
            safeLocalStorage.removeItem('user');
            safeLocalStorage.removeItem('token');
            safeLocalStorage.removeItem('auth-storage');
          }
          
          set({ 
            user: null, 
            isAuthenticated: false,
            isInitialized: true,
            isAdmin: false,
            isLoading: false,
            error: undefined
          });
          

        } catch (error) {
          console.error('Error clearing corrupted data:', error);
        }
      },

      initialize: () => {
        const state = get();

        
        // Run one-time cleanup of corrupted auth data
        clearCorruptedAuthData();
        
        // Set loading state during initialization
        set({ isLoading: true, error: undefined });
        
        if (typeof window !== 'undefined') {
          try {
            // First try to get from manual localStorage (OAuth callback)
            const storedUser = safeLocalStorage.getItem('user');
            const storedToken = safeLocalStorage.getItem('token');
            

            
            if (storedUser && storedToken) {
              try {
                const user = JSON.parse(storedUser);
                // Ensure user ID is a number
                if (user.id) {
                  const parsedId = parseInt(user.id, 10);
                  if (Number.isNaN(parsedId)) {
                    throw new Error('Invalid user id');
                  }
                  user.id = parsedId;
                }

                const isAdmin = Boolean(
                  user && (isAdminByEmail(user.email) || user.role === 'admin' || user.isSuperAdmin === true)
                );
                set({
                  user,
                  isAuthenticated: true,
                  isInitialized: true,
                  isAdmin,
                  isLoading: false,
                  error: undefined
                });
                return;
              } catch (parseError) {
                console.error('Failed to parse stored user data:', parseError);

                try {
                  safeLocalStorage.removeItem('user');
                  safeLocalStorage.removeItem('token');
                  safeLocalStorage.removeItem('auth-storage');
                } catch (cleanupError) {
                  console.error('Failed to clear corrupted auth data:', cleanupError);
                }
              }
            }
            
            // Also try to get from Zustand persist data
            const authStorage = safeLocalStorage.getItem('auth-storage');
            if (authStorage) {
              try {
                const authData = JSON.parse(authStorage);

                if (authData.state && authData.state.user) {
                  // Ensure user ID is a number
                  const user = authData.state.user;
                  if (user.id) {
                    const parsedId = parseInt(user.id, 10);
                    if (Number.isNaN(parsedId)) {
                      throw new Error('Invalid user id');
                    }
                    user.id = parsedId;
                  }
                  const isAdmin = Boolean(
                    user && (isAdminByEmail(user.email) || user.role === 'admin' || user.isSuperAdmin === true)
                  );
                  set({
                    user,
                    isAuthenticated: true,
                    isInitialized: true,
                    isAdmin,
                    isLoading: false,
                    error: undefined
                  });
                  return;
                }
              } catch (parseError) {
                console.error('Failed to parse Zustand auth data:', parseError);

                // Clear corrupted data
                safeLocalStorage.removeItem('auth-storage');
              }
            }
          } catch (error) {
            console.error('Error parsing stored user:', error);
            set({ 
              error: 'Failed to initialize authentication', 
              isLoading: false 
            });
          }

          // If no localStorage data found, ensure initialized is true

          set({
            isAuthenticated: false,
            isInitialized: true,
            isAdmin: false,
            isLoading: false,
            error: undefined
          });
        }
      },
      
      // API Keys actions
      // Plan and billing actions
      fetchBillingInfo: async () => {
        const state = get();
        if (!state.user?.id) return;
        
        set({ planLoading: true });
        
        try {
          const token = safeLocalStorage.getItem('token');
          if (!token || !token.startsWith('eyJ')) {
            set({ planLoading: false });
            return;
          }

          // Billing info endpoint removed - smart-lead-api feature removed
          const response = await fetch('/api/agency/billing-info', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            set({ 
              billingInfo: data,
              planLoading: false 
            });
            
            // Also fetch the plan details if we have a plan name
            if (data.planName) {
              // Convert plan name to plan ID for fetching details
              const { pricingData } = await import('./pricing-data');
              const plan = pricingData.plans.find(p => p.name.toLowerCase() === data.planName.toLowerCase());
              if (plan) {
                await state.fetchPlanDetails(plan.id);
              }
            }
          } else {
            console.error('Failed to fetch billing info:', response.status, response.statusText);
            set({ planLoading: false });
          }
        } catch (error) {
          console.error('Error fetching billing info:', error);
          set({ planLoading: false });
        }
      },
      
      setBillingInfo: (info) => set({ billingInfo: info }),
      setActivePlan: (plan) => set({ activePlan: plan }),
      
      fetchPlanDetails: async (planId) => {
        try {
          // Import pricing data to find the plan
          const { pricingData } = await import('./pricing-data');
          const plan = pricingData.plans.find(p => p.id === planId);
          if (plan) {
            set({ activePlan: plan });
          }
        } catch (error) {
          console.error('Error fetching plan details:', error);
        }
      },
      
      updatePlan: async (planId) => {
        const state = get();
        if (!state.user?.id) return false;
        
        try {
          const token = safeLocalStorage.getItem('token');
          if (!token || !token.startsWith('eyJ')) return false;



          // Update plan endpoint removed - smart-lead-api feature removed
          const response = await fetch('/api/agency/update-plan', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              planId: planId
            })
          });



          if (response.ok) {
            const result = await response.json();

            // Refresh billing info after plan change
            await state.fetchBillingInfo();
            return true;
          } else {
            const error = await response.json();
            console.error('Plan update failed:', error);
            return false;
          }
        } catch (error) {
          console.error('Error updating plan:', error);
          return false;
        }
      },
      
      // Debounced auth actions
      debouncedInitialize: debounce(() => {
        const state = get();
        if (!state.isInitialized) {
          state.initialize();
        }
      }, 300), // 300ms debounce
      
      debouncedRefreshAuth: debounce(async () => {
        const state = get();
        if (!state.user?.id) return;
        
        try {
          set({ isLoading: true, error: undefined });
          
          const token = safeLocalStorage.getItem('token');
          if (!token || !token.startsWith('eyJ')) {
            set({ isLoading: false });
            return;
          }

          const response = await fetch('/api/auth/verify', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const userData = await response.json();
            const isAdmin = Boolean(
              (userData.email && isAdminByEmail(userData.email)) ||
              userData.role === 'admin' ||
              userData.isSuperAdmin === true
            );
            set({
              user: userData,
              isAuthenticated: true,
              isAdmin,
              isLoading: false,
              error: undefined
            });
          } else {
            set({ 
              user: null, 
              isAuthenticated: false, 
              isAdmin: false,
              isLoading: false,
              error: 'Authentication expired'
            });
          }
        } catch (error) {
          console.error('Error refreshing auth:', error);
          set({ 
            error: 'Failed to refresh authentication', 
            isLoading: false 
          });
        }
      }, 500) // 500ms debounce for auth refresh
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => createSafeStorage()),
      partialize: (state) => {
        return {
          user: state.user,
          isAuthenticated: !!state.user,
          // isInitialized intentionally NOT persisted — persisting it causes a
          // server/client hydration mismatch: SSR always renders isInitialized=false
          // (no localStorage), but the client would rehydrate it as true, causing
          // React to throw a hydration error and trigger the ErrorBoundary.
          isAdmin: state.isAdmin,
        };
      },
    }
  )
); 
